import { InviteUserParams } from '@/core/api/collections/users';
import { LicensesRemaining } from '@/core/auth/AuthContext';
import { validateLicenseType } from '@/core/users/UserPermissions/userEditValidators';

const MAX_EMAILS = 100;
export const UserInviteError = {
  PRODUCT_REQUIRED: 'At least one product must be enabled',
  EMAILS_REQUIRED: 'At least one email is required',
  EMAILS_MAX_LIMIT: `Only ${MAX_EMAILS} users can be invited at a time`,
  EMAILS_INVALID_FORMAT: 'Invalid formatting (separate multiple email addresses with a comma)',
  REPLY_TO_REQUIRED: 'An email address is required',
  REPLY_TO_INVALID_FORMAT: 'Invalid reply-to email address',
};

export type UserInviteFormData = InviteUserParams & {
  commaSeparatedEmails: string;
}
export type UserInviteErrors = { general?: string; } & {
  [key in keyof UserInviteFormData ]?: string;
};

export function validateUserInvite(
  { core, uvk, commaSeparatedEmails, replyTo }: UserInviteFormData,
  licensesRemaining: LicensesRemaining,
) {
  let errors = {} as UserInviteErrors;

  if (commaSeparatedEmails) {
    const emails = sanitizeCommaSeparatedEmailInput(commaSeparatedEmails).split(/,\s*/);

    if (emails.length > MAX_EMAILS) {
      errors.emails = UserInviteError.EMAILS_MAX_LIMIT;
    } else {
      const validEmails: Record<string, boolean> = {};
      emails.forEach((email: string, index: number) => {
        if (email === '' || email.indexOf(' ') !== -1) {
          errors.emails = UserInviteError.EMAILS_INVALID_FORMAT;
        } else if (!isEmailValid(email)) {
          errors.emails = emails.length === 1 ? 'Invalid email address' : `Email #${index + 1} is invalid: ${email}`;
        } else if (validEmails[email.toLowerCase()]) {
          errors.emails = `${email} is listed more than once`;
        } else {
          validEmails[email.toLowerCase()] = true;
        }
      });
      const licenseTypeError = core?.licenseType ? validateLicenseType(core.licenseType, licensesRemaining, emails.length) : null;
      if (licenseTypeError) {
        errors.core = licenseTypeError;
      }
    }
  } else {
    errors.emails = UserInviteError.EMAILS_REQUIRED;
  }

  if (!replyTo) {
    errors.replyTo = UserInviteError.REPLY_TO_REQUIRED;
  } else if (!isEmailValid(replyTo)) {
    errors.replyTo = UserInviteError.REPLY_TO_INVALID_FORMAT;
  }

  if (!core && !uvk) {
    errors.general = UserInviteError.PRODUCT_REQUIRED;
  }
  return errors;
}

const EMAIL_REGEXP = /([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+)/;
function isEmailValid(email: string) {
  return email.match(EMAIL_REGEXP);
}

export function sanitizeCommaSeparatedEmailInput (emails: string) {
  return emails.trim().replace(/(,$)/g, '');
}
